import type { ChangeEvent } from 'react'
import { Select, SelectOption } from '@/common/ui/Form/Select'
import { cn } from '@/common/lib/cn/cn'
import { BRAND_COMPARE_COLORS } from '../../lib/brandComparison'
import type { CompanyStatsComparisonMode } from '../../model/useCompanyStatsBrandComparison'

export type BrandComparisonOption = {
    id: string
    name: string
    productCount?: number
}

type CompanyStatsBrandComparisonHeaderProps = {
    compareBy: CompanyStatsComparisonMode
    entityAId: string
    entityBId: string
    entityAName: string
    entityBName: string
    entityOptions: BrandComparisonOption[]
    onCompareByChange: (e: ChangeEvent<HTMLSelectElement>) => void
    onEntityAChange: (e: ChangeEvent<HTMLSelectElement>) => void
    onEntityBChange: (e: ChangeEvent<HTMLSelectElement>) => void
}

const formatBrandLabel = (brand: BrandComparisonOption) => {
    if (brand.productCount != null) {
        return `${brand.name} ${brand.productCount} тов.`
    }
    return brand.name
}

export const CompanyStatsBrandComparisonHeader = ({
    compareBy,
    entityAId,
    entityBId,
    entityAName,
    entityBName,
    entityOptions,
    onCompareByChange,
    onEntityAChange,
    onEntityBChange,
}: CompanyStatsBrandComparisonHeaderProps) => {
    const entityLabel = compareBy === 'brand' ? 'бренд' : 'продавец'
    const entityPlaceholderLabel = compareBy === 'brand' ? 'бренд' : 'продавца'
    const compareDescription =
        compareBy === 'brand'
            ? 'Сопоставление показателей контент-аудита по брендам'
            : 'Сопоставление показателей контент-аудита по продавцам'
    const shouldShowLegend = Boolean(entityAId && entityBId)

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
                <p className=''>{compareDescription}</p>
            </div>

            <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
                <div className='flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center'>
                    <Select
                        value={compareBy}
                        onChange={onCompareByChange}
                        aria-label='Тип сравнения'
                        size='lg'
                        className='min-w-[180px]'
                    >
                        <SelectOption value='brand'>Бренды</SelectOption>
                        <SelectOption value='seller'>Продавцы</SelectOption>
                    </Select>
                    <Select
                        value={entityAId}
                        onChange={onEntityAChange}
                        placeholder={`Выберите ${entityPlaceholderLabel}`}
                        aria-label={`Первый ${entityLabel} для сравнения`}
                        size='lg'
                        className='min-w-[200px]'
                    >
                        {entityOptions.map(brand => (
                            <SelectOption key={brand.id} value={brand.id} disabled={brand.id === entityBId}>
                                {formatBrandLabel(brand)}
                            </SelectOption>
                        ))}
                    </Select>
                    <span className='text-sm font-medium text-base-content/50 text-center' aria-hidden>
                        vs
                    </span>
                    <Select
                        value={entityBId}
                        onChange={onEntityBChange}
                        placeholder={`Выберите ${entityPlaceholderLabel}`}
                        aria-label={`Второй ${entityLabel} для сравнения`}
                        size='lg'
                        className='min-w-[200px]'
                    >
                        {entityOptions.map(brand => (
                            <SelectOption key={brand.id} value={brand.id} disabled={brand.id === entityAId}>
                                {formatBrandLabel(brand)}
                            </SelectOption>
                        ))}
                    </Select>
                </div>

                {shouldShowLegend ? (
                    <div className='flex items-center gap-4 text-sm text-base-content/70'>
                        <span className='inline-flex items-center gap-2'>
                            <span
                                className='inline-block h-2 w-2 rounded-full'
                                style={{ backgroundColor: BRAND_COMPARE_COLORS.brandA }}
                                aria-hidden
                            />
                            {entityAName}
                        </span>
                        vs
                        <span className='inline-flex items-center gap-2'>
                            <span
                                className='inline-block h-2 w-2 rounded-full'
                                style={{ backgroundColor: BRAND_COMPARE_COLORS.brandB }}
                                aria-hidden
                            />
                            {entityBName}
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    )
}

export const CompanyStatsBrandComparisonLegendDot = ({
    side,
    className,
}: {
    side: 'brandA' | 'brandB'
    className?: string
}) => (
    <span
        className={cn('inline-block h-2 w-2 shrink-0 rounded-full', className)}
        style={{ backgroundColor: BRAND_COMPARE_COLORS[side] }}
        aria-hidden
    />
)
